import { useEffect, useRef, useState } from 'react'
import {
  SocialTopicFilters,
  SocialTopicPostItem,
  SocialTopicPostsSort,
} from '@digital-twins/shared-types'
import { useSocialListeningApi } from './SocialListeningApiContext'
import { socialTopicFiltersCacheKey } from './useSocialTopicFilters'

export const SOCIAL_UNIVERSE_POSTS_PAGE_SIZE = 30

interface UseSocialUniversePostsOptions {
  filters?: SocialTopicFilters
  sort?: SocialTopicPostsSort
  /** Page size; a Theme section asks for a few tiles, "See all" for full pages. */
  limit?: number
  /**
   * Reloads from the first page when it changes (for example the Topic's
   * `lastSyncedAt`). The loaded posts stay on screen until the new page
   * arrives, so a refresh never flashes skeletons.
   */
  refreshKey?: string | number | null
}

interface UseSocialUniversePostsResult {
  posts: Array<SocialTopicPostItem>
  isLoading: boolean
  isLoadingMore: boolean
  hasError: boolean
  hasMore: boolean
  loadMore: () => void
  /** Drops posts from the loaded pages (after hide, remove post or remove author). */
  removePosts: (predicate: (post: SocialTopicPostItem) => boolean) => void
}

/**
 * Cursor-paginated posts of one Topic through the read layer (hidden posts are
 * never listed). Reloads from the first page when the Topic, the filters, the
 * order, the page size or `refreshKey` change; a stale response is dropped.
 */
export function useSocialUniversePosts(
  universeToken: string | null,
  options: UseSocialUniversePostsOptions = {},
): UseSocialUniversePostsResult {
  const { filters, sort = 'newest', limit = SOCIAL_UNIVERSE_POSTS_PAGE_SIZE, refreshKey = null } =
    options
  const socialListeningApi = useSocialListeningApi()
  const [posts, setPosts] = useState<Array<SocialTopicPostItem>>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [hasError, setHasError] = useState(false)
  const generationRef = useRef(0)
  const filtersKey = socialTopicFiltersCacheKey(filters)
  const filtersRef = useRef(filters)
  filtersRef.current = filters
  const queryKey = JSON.stringify([universeToken, filtersKey, sort, limit])
  const lastQueryKeyRef = useRef<string | null>(null)

  useEffect(() => {
    const generation = ++generationRef.current
    // Only `refreshKey` moved: same query, so keep the posts while reloading.
    const isRefreshOnly = lastQueryKeyRef.current === queryKey
    lastQueryKeyRef.current = queryKey
    if (!isRefreshOnly) {
      setPosts([])
      setNextCursor(null)
    }
    setHasError(false)
    setIsLoadingMore(false)
    if (!universeToken) {
      setIsLoading(false)
      return
    }
    setIsLoading(!isRefreshOnly)
    socialListeningApi
      .listUniversePosts(universeToken, { filters: filtersRef.current, sort, limit })
      .then((page) => {
        if (generation !== generationRef.current) {
          return
        }
        setPosts(page.posts)
        setNextCursor(page.nextCursor)
      })
      .catch(() => {
        if (generation === generationRef.current) {
          setHasError(true)
        }
      })
      .finally(() => {
        if (generation === generationRef.current) {
          setIsLoading(false)
        }
      })
  }, [socialListeningApi, universeToken, filtersKey, sort, limit, refreshKey, queryKey])

  function loadMore() {
    if (!universeToken || !nextCursor || isLoadingMore) {
      return
    }
    const generation = generationRef.current
    setIsLoadingMore(true)
    socialListeningApi
      .listUniversePosts(universeToken, {
        filters: filtersRef.current,
        sort,
        limit,
        cursor: nextCursor,
      })
      .then((page) => {
        if (generation !== generationRef.current) {
          return
        }
        setPosts((prev) => [...prev, ...page.posts])
        setNextCursor(page.nextCursor)
      })
      .catch(() => {
        if (generation === generationRef.current) {
          setHasError(true)
        }
      })
      .finally(() => {
        if (generation === generationRef.current) {
          setIsLoadingMore(false)
        }
      })
  }

  function removePosts(predicate: (post: SocialTopicPostItem) => boolean) {
    setPosts((prev) => prev.filter((post) => !predicate(post)))
  }

  return {
    posts,
    isLoading,
    isLoadingMore,
    hasError,
    hasMore: Boolean(nextCursor),
    loadMore,
    removePosts,
  }
}
